import './gridHeaderFilter.less'

import React from "react"
import {observer} from "mobx-react"
import {makeAutoObservable, makeObservable, observable} from "mobx"
import {FilterFilled} from "@ant-design/icons"


import {GridColumn} from "controls/grid/gridColumnConfig"
import {FormEntry} from "controls/react/form"
import {Section} from "controls/react/layout/section"
import {Toolbar} from "controls/react/layout/toolbar"
import {AntButton} from "controls/react/ant/antButton"
import {AntPopover} from "controls/react/ant/antPopover"
import {GridDataEntry} from "controls/grid/gridDataEntry"
import {isValid, RuleDefinition} from "controls/queryBuilder/ruleDefinition"
import {QueryBuilderRuleOperator, QueryBuilderRuleValue} from 'controls/queryBuilder/queryBuilder'
import {copyViaSerializr} from "framework/serializr-integration";
import {MobxManager} from "framework/mobx-integration";
import { FormEntryNew } from "controls/react/form/formEntryNew"
import {useStore} from "core/react/useStore";


const i = require('core/localization/localization').translator({
	'Custom filtering applied message': {
		en: 'It looks like custom filtering is applied in view settings so we cant show a simple filter editor for the column.'
	}
});

type GridHeaderFilterProps<DataItem extends GridDataEntry> = {
	column: GridColumn<DataItem>
}

const b = require('b_').with('grid-header-filter')

class GridHeaderFilterStore{
	constructor(public operator: string, public value: string|string[]) {
		makeAutoObservable(this)
	}
}

type GridHeaderFilterState = {
	visible: boolean
}

export const GridHeaderFilter = observer(
	class GridHeaderFilterInner<DataItem extends GridDataEntry> extends React.Component<GridHeaderFilterProps<DataItem>, GridHeaderFilterState>{

		constructor(props: GridHeaderFilterProps<DataItem>) {
			super(props);

			this.state = {
				visible: false
			}
		}

		render() {
			if (!this.props.column.filteringEnabled)
				return null

			return <div onClick={this.toggleVisibility}>
				<AntPopover open={this.state.visible}
				            trigger={"click"}
				            content={<GridHeaderFilterContent column={this.props.column} hidePopup={this.hidePopup}/>}
				            destroyTooltipOnHide={true}
				            onOpenChange={newVisibile => this.setState({visible: newVisibile})}
				            placement={"bottom"}>
					<FilterFilled/>
				</AntPopover>
			</div>
		}

		toggleVisibility = (e: React.MouseEvent) => {
			this.setState({visible: !this.state.visible})

			e.preventDefault()
			e.stopPropagation()
		}

		hidePopup = () => {
			this.setState({visible: false})
		}
})

type GridHeaderFilterContentProps<DataEntry extends GridDataEntry> = GridHeaderFilterProps<DataEntry> & {hidePopup: () => void}

const GridHeaderFilterContent = observer(
	class GridHeaderFilterContentInner<DataEntry extends GridDataEntry> extends React.Component<GridHeaderFilterContentProps<DataEntry>>{
		filterCopy: RuleDefinition

		constructor(props: GridHeaderFilterContentProps<DataEntry>) {
			super(props)

			if(this.props.column.store.customFiltering)
				return

			if(this.props.column.filter)
				this.filterCopy = copyViaSerializr(this.props.column.filter)
			else
			{
				this.filterCopy = RuleDefinition.emptyRule(
					this.props.column.field,
					this.filterDescription.operators[0].value)
			}
		}

		get filterDescription(){
			return this.props.column.filterDescription
		}

		render(){
			if(this.props.column.store.customFiltering){
				return <Section className={b()}>
					<p>{i('Custom filtering applied message')}</p>
				</Section>
			}
			return <Section className={b()} childrenPadding={true} onClick={this.preventPropagation}>
				<FormEntryNew label={i("Operator")} model={this.filterCopy.properties} modelField={"operator"}>
					<QueryBuilderRuleOperator rule={this.filterCopy}
					                          config={this.props.column.store.filtersConfigurationEffective}/>
				</FormEntryNew>
				<FormEntryNew label={i("Value")} model={this.filterCopy.properties} modelField={'value'}>
					<QueryBuilderRuleValue rule={this.filterCopy}
					                       config={this.props.column.store.filtersConfigurationEffective}
					                       onPressEnter={this.updateFilter}
					                       onPressEsc={this.props.hidePopup}
					/>
				</FormEntryNew>
				<Toolbar appearance={"transparent"}>
					<AntButton onClick={this.clearFilter}>{i('Reset')}</AntButton>
					<AntButton type={"primary"} onClick={this.updateFilter}>{i('Ok')}</AntButton>
				</Toolbar>
			</Section>
		}

		preventPropagation = (e: React.MouseEvent) => {
			e.stopPropagation()
		}

		updateFilter = () => {
			this.props.column.updateFilter(this.filterCopy)
			this.props.hidePopup()
		}

		clearFilter = () => {
			this.props.column.clearFilter()
			this.props.hidePopup()
		}
	}
)

class GridHeaderInlineFilterStore<T> {
	filter: RuleDefinition
	mobx = new MobxManager();

	constructor( public props: GridHeaderFilterProps<T>) {
		makeAutoObservable(this);
	}

	init() {
		this.filter = this.props.column.filter ??  RuleDefinition.emptyRule(
			this.props.column.field,
			this.props.column.filterDescription.operators[0].value);

		this.mobx.reaction(() => isValid(this.filter), (value) => {
			this.props.column.updateFilter(this.filter);
		});

		this.mobx.reaction(() => this.props.column.filter, (value) => {
			if(this.props.column.filter != this.filter){
				if( this.props.column.filter != null) {
					this.filter = this.props.column.filter;
				}else{
					this.filter = RuleDefinition.emptyRule(
						this.props.column.field,
						this.props.column.filterDescription.operators[0].value);
				}
			}
		});
	}

	destroy() {
		this.mobx.destroy();
	}
}

export const GridHeaderInlineFilter = observer(<DataEntry extends GridDataEntry>(props: GridHeaderFilterProps<DataEntry>) => {
	const store = useStore(() => new GridHeaderInlineFilterStore<DataEntry>(props));

	if(store.filter == null)
		return null;

	return <QueryBuilderRuleValue rule={store.filter}
	                              config={props.column.store.filtersConfigurationEffective}
	                              forceOneLineLayout={true}
	                              bounced={1000}
	/>
})
